import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { Loader2 } from "lucide-react";
import { Redirect, Route } from "wouter";
import { normalizeRoles, hasRole } from "@/lib/normalize-user-data";
import { AdminPanelGuard } from "@/components/feature-guards/AdminPanelGuard";
import { SellerPanelGuard } from "@/components/feature-guards/SellerPanelGuard";
import { InstructorPanelGuard } from "@/components/feature-guards/InstructorPanelGuard";
import { CertificateAdminGuard } from "@/components/feature-guards/CertificateAdminGuard";
import { ProfessionalPanelGuard } from "@/components/feature-guards/ProfessionalPanelGuard";
import {
  canAccessInstructorPanel,
  canAccessProfessionalJobsProfile,
  canAccessSellerPanel,
} from "@/lib/panel-switcher-access";

type PanelRouteType =
  | "curriculum"
  | "course"
  | "admin"
  | "control"
  | "instructor"
  | "course-authoring"
  | "professional"
  | "auto";

function panelRouteUsesAuthReturnTo(panelType?: PanelRouteType): boolean {
  return !!panelType && panelType !== "auto";
}

/** Preserves deep links when sending unauthenticated users to sign-in. */
function PanelUnauthRedirect({
  passReturnTo,
  fallbackPath,
}: {
  passReturnTo: boolean;
  fallbackPath: string;
}) {
  const returnTarget =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : fallbackPath;

  const to = passReturnTo
    ? `/auth?returnTo=${encodeURIComponent(returnTarget)}`
    : "/auth";

  return <Redirect to={to} />;
}

/**
 * Determines which panel a user should access based on their roles and subscription tier
 * NEW LOGIC:
 * Priority: ADMIN role > SELLER+seller tier > PROFESSIONAL+professional tier > INSTRUCTOR > default dashboard
 * 
 * Access Matrix:
 * - ADMIN role: /dashboard/user (admin UI moved to external app)
 * - SELLER/PREMIUM_SELLER + seller tier: /seller
 * - PROFESSIONAL + professional tier: /professional
 * - INSTRUCTOR: /instructor
 * - No panel role: /dashboard
 */
export function getPanelForUserWithTier(user: any, subscriptionData: any): string {
  if (!user) return "/auth";

  const isAdmin = hasRole(user, 'ADMIN');
  
  // Admin uses dashboard in this app (admin UI moved to external app)
  if (isAdmin) {
    return "/dashboard/user";
  }
  
  if (canAccessSellerPanel(user, subscriptionData || {})) {
    return "/seller";
  }

  if (canAccessProfessionalJobsProfile(user, subscriptionData || {})) {
    return "/professional";
  }

  // Instructor is checked LAST among the panels. It used to run before the
  // seller check, so anyone with INSTRUCTOR on an Imperial plan was
  // routed into it by default and never saw their Seller panel. Dual-role
  // users reach it from the panel switcher / navbar instead.
  if (canAccessInstructorPanel(user, subscriptionData || {})) {
    return "/instructor";
  }
  
  // Default to user dashboard for FREE tier
  return "/dashboard";
}

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use getPanelForUserWithTier instead
 */
export function getPanelForUser(user: any): string {
  if (!user) return "/auth";

  const roles = normalizeRoles(user);

  if (roles.includes("ADMIN")) {
    return "/dashboard/user";
  }

  if (roles.includes("SELLER") || roles.includes("PREMIUM_SELLER")) {
    return "/curriculum-panel";
  }
  if (roles.includes("INSTRUCTOR")) {
    return "/course-panel";
  }

  return "/dashboard";
}

/**
 * New Enhanced Route component with Feature Guards
 * Uses the new tier-based and role-based feature guards
 */
export function PanelRoute({
  path,
  component: Component,
  panelType,
}: {
  path: string;
  component: () => React.JSX.Element;
  panelType?: PanelRouteType;
}) {
  const { user, isLoading } = useFirebaseAuth();

  if (isLoading) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-border" />
        </div>
      </Route>
    );
  }

  if (!user) {
    return (
      <Route path={path}>
        <PanelUnauthRedirect
          passReturnTo={panelRouteUsesAuthReturnTo(panelType)}
          fallbackPath={path}
        />
      </Route>
    );
  }

  // Apply appropriate feature guard based on panel type
  let content: React.ReactNode;
  switch (panelType) {
    case 'admin':
      content = (
        <AdminPanelGuard>
          <Component />
        </AdminPanelGuard>
      );
      break;

    case 'control':
      content = (
        <SellerPanelGuard>
          <Component />
        </SellerPanelGuard>
      );
      break;

    case 'instructor':
      content = (
        <InstructorPanelGuard>
          <Component />
        </InstructorPanelGuard>
      );
      break;

    case 'course-authoring':
      content = (
        <CertificateAdminGuard>
          <Component />
        </CertificateAdminGuard>
      );
      break;

    case 'professional':
      content = (
        <ProfessionalPanelGuard>
          <Component />
        </ProfessionalPanelGuard>
      );
      break;

    // Legacy panel types - keep existing role-based logic for now
    case 'curriculum':
    case 'course': {
      const roles = normalizeRoles(user);
      let hasAccess = false;

      if (panelType === 'curriculum') {
        hasAccess = roles.some(role =>
          role === 'INSTRUCTOR' ||
          role === 'ADMIN'
        );
      } else if (panelType === 'course') {
        hasAccess = roles.some(role =>
          role === 'INSTRUCTOR' ||
          role === 'ADMIN'
        );
      }

      if (!hasAccess) {
        const correctPanel = getPanelForUser(user);
        content = <Redirect to={correctPanel} />;
      } else {
        content = <Component />;
      }
      break;
    }

    default:
      content = <Component />;
  }

  return (
    <Route path={path}>
      {content}
    </Route>
  );
}

/**
 * Smart panel redirect - redirects /panel to the appropriate panel based on user role and tier
 * Uses the new tier-based logic
 */
export function SmartPanelRedirect() {
  const { user, isLoading } = useFirebaseAuth();
  const subscriptionData = useSubscription();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-border" />
      </div>
    );
  }
  
  if (!user) {
    const returnTarget =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "/panel";
    return (
      <Redirect to={`/auth?returnTo=${encodeURIComponent(returnTarget)}`} />
    );
  }

  // Use new tier-based panel determination
  const panelPath = getPanelForUserWithTier(user, subscriptionData);
  
  console.log('🎯 SmartPanelRedirect:', {
    user: user?.email,
    tier: subscriptionData?.planName || 'Free',
    redirectTo: panelPath
  });
  
  return <Redirect to={panelPath} />;
}
